<template>
  <!-- ── Desktop ──────────────────────────────────────────────────── -->
  <div class="hidden sm:flex sm:flex-col sm:h-screen bg-gray-50 dark:bg-[#0d1117]">

    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-6 py-3.5">
        <h1 class="text-sm font-semibold text-gray-900 dark:text-white">Analytics</h1>
        <div class="flex items-center gap-3">
          <button @click="refresh" :disabled="refreshing" title="Refresh"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40">
            <svg class="w-4 h-4" :class="refreshing ? 'animate-spin' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <div class="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button v-for="d in DURATIONS" :key="d.value" @click="duration = d.value"
              :class="duration === d.value
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
              class="px-3 py-1.5 text-xs font-semibold rounded-md transition-all">
              {{ d.label }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex px-6 gap-1">
        <button @click="switchTab('bookings')"
          :class="activeTab === 'bookings' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
          class="px-1 pb-3 pt-0.5 mr-5 text-sm font-semibold border-b-2 transition-colors">
          Bookings
        </button>
        <button @click="switchTab('traffic')"
          :class="activeTab === 'traffic' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
          class="px-1 pb-3 pt-0.5 text-sm font-semibold border-b-2 transition-colors">
          Traffic
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto p-6">
      <TrafficTab  v-if="activeTab === 'traffic'"  :duration="duration" />
      <BookingsTab v-if="activeTab === 'bookings'" :duration="duration" />
    </div>
  </div>

  <!-- ── Mobile ───────────────────────────────────────────────────── -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Analytics</h1>
        <div class="flex items-center gap-2">
          <button @click="refresh" :disabled="refreshing"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40">
            <svg class="w-4 h-4" :class="refreshing ? 'animate-spin' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <select v-model="duration"
            class="text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border-0 rounded-lg px-2.5 py-1.5 font-semibold focus:ring-2 focus:ring-blue-500 outline-none">
            <option v-for="d in DURATIONS" :key="d.value" :value="d.value">{{ d.label }}</option>
          </select>
        </div>
      </div>
      <div class="flex border-t border-gray-100 dark:border-gray-800">
        <button @click="switchTab('bookings')"
          :class="activeTab === 'bookings' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400'"
          class="flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors">
          Bookings
        </button>
        <button @click="switchTab('traffic')"
          :class="activeTab === 'traffic' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400'"
          class="flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors">
          Traffic
        </button>
      </div>
    </div>

    <div class="pb-20 pt-4 px-4 space-y-4">
      <TrafficTab  v-if="activeTab === 'traffic'"  :duration="duration" :mobile="true" />
      <BookingsTab v-if="activeTab === 'bookings'" :duration="duration" :mobile="true" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter }              from 'vue-router'
import { useGlobalStore }                   from 'stores/global.js'
import { useAnalyticsTrafficStore }         from 'stores/analyticsTraffic.js'
import { useAnalyticsBookingsStore }        from 'stores/analyticsBookings.js'
import TrafficTab  from './traffic/TrafficTab.vue'
import BookingsTab from './bookings/BookingsTab.vue'

const DURATIONS = [
  { value: '24_hours', label: '24h' },
  { value: '7_days',   label: '7d'  },
  { value: '2_weeks',  label: '2w'  },
  { value: '1_month',  label: '1mo' },
  { value: '3_months', label: '3mo' },
  { value: '6_months', label: '6mo' },
  { value: '1_year',   label: '1yr' },
]

const route    = useRoute()
const router   = useRouter()
const traffic  = useAnalyticsTrafficStore()
const bookings = useAnalyticsBookingsStore()

const duration   = ref('7_days')
const refreshing = ref(false)

const activeTab = computed(() => route.path.includes('/traffic') ? 'traffic' : 'bookings')

function switchTab(tab) {
  router.push(tab === 'traffic' ? '/analytics/traffic' : '/analytics')
}

async function refresh() {
  refreshing.value = true
  const store = activeTab.value === 'traffic' ? traffic : bookings
  store.invalidate(duration.value)
  await store.fetchAll(duration.value)
  refreshing.value = false
}

onMounted(() => {
  useGlobalStore().fetchCurrentUser()
})
</script>
